import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../supabaseClient';

/**
 * Client Portal — replaces the old ResultsPage.
 * First visit (report pending): shows confirmation + polling.
 * Return visit (report ready): dashboard with download, Voice-Brand card, share placeholder.
 */
function ClientPortal() {
  const { user, profile, loading: authLoading, signOut } = useAuth();
  const [reportStatus, setReportStatus] = useState(null);
  const [reportUrl, setReportUrl] = useState(null);
  const [profileTitle, setProfileTitle] = useState(null);
  const [reportDate, setReportDate] = useState(null);
  const [polling, setPolling] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Voice-Brand state
  const [voiceBrandPurchased, setVoiceBrandPurchased] = useState(false);
  const [voiceBrandInputs, setVoiceBrandInputs] = useState([]); // all voice_brand_inputs rows for this user
  const [voiceBrandPolling, setVoiceBrandPolling] = useState(false);

  // TDA state
  const [tdaPurchased, setTdaPurchased] = useState(false);
  const [tdaTeams, setTdaTeams] = useState([]);
  const [tdaPolling, setTdaPolling] = useState(false);

  // TDA checkout state
  const [tdaTeamSize, setTdaTeamSize] = useState(4);
  const [tdaCheckingOut, setTdaCheckingOut] = useState(false);
  const [tdaCheckoutError, setTdaCheckoutError] = useState('');

  // Course access state (Master Your PASS Profile, sold as the $59.99 course bundle).
  // Access lives in the course library's course_purchases table and is keyed on the
  // email used at Stripe checkout, so it has to be looked up server-side.
  const [courseAccess, setCourseAccess] = useState(null);

  // Share state
  const [shareContent, setShareContent] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareExpanded, setShareExpanded] = useState(false);

  // Stripe links for Voice-Brand add-on purchases
  const VB_STANDALONE_LINK = 'https://buy.stripe.com/bJefZif5jgOL6i15CN4wM0c';
  const VB_ADDITIONAL_RUN_LINK = 'https://buy.stripe.com/8x200kcXbaqnfSBc1b4wM0e';

  // Initialize from profile once auth loads
  useEffect(() => {
    if (profile) {
      setReportStatus(profile.report_status);
      setReportUrl(profile.report_url);
      setProfileTitle(profile.profile_title);
      setReportDate(profile.report_generated_at);
      setVoiceBrandPurchased(profile.voice_brand_purchased === true);
      setTdaPurchased(profile.tda_purchased === true);
      setShareContent(profile.share_content || null);
      setProfileLoaded(true);
    } else if (!authLoading && user) {
      setProfileLoaded(true);
    }
  }, [profile, authLoading, user]);

  // Check whether this user has the Master Your PASS Profile course
  useEffect(() => {
    if (!user || !profileLoaded) return;

    let cancelled = false;

    const fetchCourseAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch('/.netlify/functions/get-course-access', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: '{}',
        });
        if (!response.ok) return;

        const data = await response.json();
        if (!cancelled && data.hasCourse) setCourseAccess(data);
      } catch (err) {
        // Non-critical — the buyer also got this link by email at purchase.
        console.error('Course access check failed:', err);
      }
    };

    fetchCourseAccess();
    return () => { cancelled = true; };
  }, [user, profileLoaded]);

  // Fetch voice_brand_inputs for this user
  useEffect(() => {
    if (!user || !profileLoaded) return;

    const fetchVoiceBrandInputs = async () => {
      const { data, error } = await supabase
        .from('voice_brand_inputs')
        .select('id, job_title, report_url, report_status, generated_at, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setVoiceBrandInputs(data);
      }
    };

    fetchVoiceBrandInputs();
  }, [user, profileLoaded]);

  // Fetch TDA teams for this user (as leader)
  useEffect(() => {
    if (!user || !profileLoaded || !tdaPurchased) return;

    const fetchTdaTeams = async () => {
      const { data, error } = await supabase
        .from('tda_teams')
        .select('id, team_name, report_status, report_url, created_at')
        .eq('leader_user_id', user.id)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setTdaTeams(data);
      }
    };

    fetchTdaTeams();
  }, [user, profileLoaded, tdaPurchased]);

  // Poll for TDA report completion if any team is 'generating'
  useEffect(() => {
    const hasGenerating = tdaTeams.some(
      (t) => t.report_status === 'generating'
    );

    if (!hasGenerating || !user) return;

    setTdaPolling(true);
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('tda_teams')
        .select('id, team_name, report_status, report_url, created_at')
        .eq('leader_user_id', user.id)
        .order('created_at', { ascending: false });

      if (data) {
        setTdaTeams(data);
        const stillGenerating = data.some(
          (t) => t.report_status === 'generating'
        );
        if (!stillGenerating) {
          setTdaPolling(false);
          clearInterval(interval);
        }
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [tdaTeams, user]);

  // Poll for voice-brand report completion if any input is 'generating' or 'pending'
  useEffect(() => {
    const hasGenerating = voiceBrandInputs.some(
      (input) => input.report_status === 'generating' || input.report_status === 'pending'
    );

    if (!hasGenerating || !user) return;

    setVoiceBrandPolling(true);
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('voice_brand_inputs')
        .select('id, job_title, report_url, report_status, generated_at, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (data) {
        setVoiceBrandInputs(data);
        const stillGenerating = data.some(
          (input) => input.report_status === 'generating' || input.report_status === 'pending'
        );
        if (!stillGenerating) {
          setVoiceBrandPolling(false);
          clearInterval(interval);
        }
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [voiceBrandInputs, user]);

  // Poll for report completion when status is pending
  useEffect(() => {
    if (profileLoaded && reportStatus && reportStatus !== 'completed') {
      setPolling(true);
      const interval = setInterval(async () => {
        const { data } = await supabase
          .from('users')
          .select('report_status, report_url, profile_title, report_generated_at')
          .eq('id', user.id)
          .single();

        if (data && data.report_status === 'completed') {
          setReportStatus('completed');
          setReportUrl(data.report_url);
          setProfileTitle(data.profile_title);
          setReportDate(data.report_generated_at);
          setPolling(false);
          clearInterval(interval);
        }
      }, 15000); // Check every 15 seconds

      return () => clearInterval(interval);
    }
  }, [reportStatus, user, profileLoaded]);

  // Wait for both auth AND profile data before showing content
  if (authLoading || (!profileLoaded && user)) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading your portal...</p>
      </div>
    );
  }

  const reportReady = reportStatus === 'completed' && reportUrl;

  // Determine voice-brand state for the card
  const completedInputs = voiceBrandInputs.filter((i) => i.report_status === 'completed');
  const generatingInputs = voiceBrandInputs.filter(
    (i) => i.report_status === 'generating' || i.report_status === 'pending'
  );
  const hasCompletedVB = completedInputs.length > 0;
  const hasGeneratingVB = generatingInputs.length > 0;
  const hasAnyInput = voiceBrandInputs.length > 0;

  // Detect stuck pending rows (submitted > 10 min ago, still pending)
  const isStuckPending = (input) => {
    if (input.report_status !== 'pending') return false;
    const created = new Date(input.created_at);
    const minutesAgo = (Date.now() - created.getTime()) / 60000;
    return minutesAgo > 10;
  };

  // Delete stuck row and redirect to form for retry
  const handleRetryVoiceBrand = async (inputId) => {
    await supabase.from('voice_brand_inputs').delete().eq('id', inputId);
    window.location.href = '/results/voice-brand';
  };

  const renderVoiceBrandCard = () => {
    // State 1: Not purchased — hide entirely until product is ready
    if (!voiceBrandPurchased) {
      return null;
    }

    // State 3: Generating (show spinner — or retry if stuck)
    if (hasGeneratingVB) {
      const genInput = generatingInputs[0];
      const stuck = isStuckPending(genInput);

      if (stuck) {
        return (
          <div style={styles.card}>
            <div style={styles.cardIcon}>🎯</div>
            <h3 style={styles.cardTitle}>Voice-Brand Alignment</h3>
            <p style={styles.cardDescription}>
              Something went wrong generating your report{genInput.job_title ? ` for "${genInput.job_title}"` : ''}.
              Please try submitting again.
            </p>
            <button
              onClick={() => handleRetryVoiceBrand(genInput.id)}
              style={{ ...styles.downloadBtn, cursor: 'pointer', border: 'none', fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Try Again
            </button>
          </div>
        );
      }

      return (
        <div style={styles.card}>
          <div style={styles.cardIcon}>🎯</div>
          <h3 style={styles.cardTitle}>Voice-Brand Alignment</h3>
          <p style={styles.cardDescription}>
            Your Voice-Brand report{genInput.job_title ? ` for "${genInput.job_title}"` : ''} is being generated.
          </p>
          <div style={styles.vbGeneratingStatus}>
            <div style={styles.pendingSpinner}></div>
            <div>
              <p style={styles.pendingStatusText}>Generating your report...</p>
              <p style={styles.pendingStatusSub}>This usually takes 3-5 minutes. This page will update automatically.</p>
            </div>
          </div>
          {voiceBrandPolling && (
            <p style={styles.pollingNote}>Checking for your report...</p>
          )}
        </div>
      );
    }

    // State 4: Completed (at least one report done)
    if (hasCompletedVB) {
      return (
        <div style={styles.card}>
          <div style={styles.cardIcon}>🎯</div>
          <h3 style={styles.cardTitle}>Voice-Brand Alignment</h3>
          <p style={styles.cardDescription}>
            {completedInputs.length === 1
              ? `Your Voice-Brand report${completedInputs[0].job_title ? ` for "${completedInputs[0].job_title}"` : ''} is ready.`
              : `You have ${completedInputs.length} Voice-Brand reports.`}
          </p>
          {completedInputs.map((input) => (
            <div key={input.id} style={styles.vbReportRow}>
              <div style={styles.vbReportInfo}>
                <span style={styles.vbReportRole}>{input.job_title || 'Your Role'}</span>
                {input.generated_at && (
                  <span style={styles.vbReportDate}>
                    {new Date(input.generated_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'short', day: 'numeric'
                    })}
                  </span>
                )}
              </div>
              <a
                href={input.report_url}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.vbDownloadSmall}
              >
                Download
              </a>
            </div>
          ))}
          <div style={styles.vbActions}>
            <a href="/results/voice-brand" style={styles.vbRunAgainBtn}>
              Run for a Different Role: $9.99
            </a>
          </div>
        </div>
      );
    }

    // State 2: Purchased but no input yet
    return (
      <div style={styles.card}>
        <div style={styles.cardIcon}>🎯</div>
        <h3 style={styles.cardTitle}>Voice-Brand Alignment</h3>
        <p style={styles.cardDescription}>
          You've unlocked Voice-Brand Alignment! Tell us about your role and
          we'll show you how your full communication fingerprint plays out
          in your specific job.
        </p>
        <a href="/results/voice-brand" style={styles.downloadBtn}>
          Start Your Analysis
        </a>
      </div>
    );
  };

  // TDA pricing helper (mirrors create-tda-checkout.js)
  const getTdaTier = (size) => size <= 3 ? 'pair' : size <= 8 ? 'team' : 'large';
  const TDA_PRICES = { pair: '$39.99', team: '$79.99', large: '$119.99' };
  const TDA_TIER_LABELS = { pair: 'Pair (2-3)', team: 'Team (4-8)', large: 'Large (9-12)' };

  const handleTdaCheckout = async () => {
    setTdaCheckingOut(true);
    setTdaCheckoutError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setTdaCheckoutError('Please sign in again.');
        setTdaCheckingOut(false);
        return;
      }
      const response = await fetch('/.netlify/functions/create-tda-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ team_size: tdaTeamSize, product_variant: 'tda_only' }),
      });
      const data = await response.json();
      if (!response.ok) {
        setTdaCheckoutError(data.error || 'Checkout failed. Please try again.');
        setTdaCheckingOut(false);
        return;
      }
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      setTdaCheckoutError('Network error. Please try again.');
      setTdaCheckingOut(false);
    }
  };

  // Master Your PASS Profile course card. Only shown to people who bought the course
  // bundle. The course lives on a different domain and opens by email, so the email
  // matters as much as the link — show it plainly.
  const renderCourseCard = () => {
    if (!courseAccess?.hasCourse) return null;

    const emailDiffers =
      courseAccess.courseEmail &&
      user?.email &&
      courseAccess.courseEmail.toLowerCase() !== user.email.toLowerCase();

    return (
      <div style={styles.card}>
        <div style={styles.cardIcon}>🎓</div>
        <h3 style={styles.cardTitle}>Master Your PASS Profile</h3>
        <p style={styles.cardDescription}>
          Your eight-module course on how to read and use your PASS Profile. Self-paced,
          your progress saves automatically, and access never expires.
        </p>
        <p style={styles.courseEmailNote}>
          Click <strong>"Already purchased?"</strong> on the course page and enter{' '}
          <strong>{courseAccess.courseEmail}</strong>
          {emailDiffers && ' (the email you used at checkout, which may differ from your PASS login)'}.
        </p>
        <a
          href={courseAccess.courseUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.downloadBtn}
        >
          Open the Course
        </a>
      </div>
    );
  };

  const renderTDACard = () => {
    // ===== NOT PURCHASED: Show purchase card =====
    // VISIBILITY GATE: Set to false to hide from customers until TDA is ready for sale
    const SHOW_TDA_PURCHASE = true;
    if (!tdaPurchased) {
      if (!SHOW_TDA_PURCHASE || reportStatus !== 'completed') return null;
      const tier = getTdaTier(tdaTeamSize);
      return (
        <div style={styles.card}>
          <div style={styles.cardIcon}>&#128101;</div>
          <h3 style={styles.cardTitle}>Team Dynamics Alignment</h3>
          <p style={styles.cardDescription}>
            See how your team's communication approaches fit together, with practical steps
            for working together, built on each member's PASS Profile.
          </p>
          <div style={styles.tdaSizeSelector}>
            <label style={styles.tdaSizeLabel}>Team size:</label>
            <div style={styles.tdaSizeBtnRow}>
              {[{ label: '2-3', min: 2 }, { label: '4-8', min: 4 }, { label: '9-12', min: 9 }].map(opt => (
                <button
                  key={opt.label}
                  onClick={() => setTdaTeamSize(opt.min)}
                  style={getTdaTier(tdaTeamSize) === getTdaTier(opt.min)
                    ? { ...styles.tdaSizeBtn, ...styles.tdaSizeBtnActive }
                    : styles.tdaSizeBtn}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p style={styles.tdaPriceDisplay}>
              {TDA_TIER_LABELS[tier]}: <strong>{TDA_PRICES[tier]}</strong>
            </p>
          </div>
          {tdaCheckoutError && <p style={styles.tdaError}>{tdaCheckoutError}</p>}
          <button
            onClick={handleTdaCheckout}
            disabled={tdaCheckingOut}
            style={tdaCheckingOut ? { ...styles.downloadBtn, opacity: 0.6 } : styles.downloadBtn}
          >
            {tdaCheckingOut ? 'Redirecting to checkout...' : 'Get Started'}
          </button>
        </div>
      );
    }

    const completedTeams = tdaTeams.filter((t) => t.report_status === 'completed');
    const generatingTeams = tdaTeams.filter((t) => t.report_status === 'generating');
    const draftTeams = tdaTeams.filter(
      (t) => t.report_status !== 'completed' && t.report_status !== 'generating' && t.report_status !== 'failed'
    );
    const hasAnyTeam = tdaTeams.length > 0;

    // State: Has a team generating
    if (generatingTeams.length > 0) {
      const genTeam = generatingTeams[0];
      return (
        <div style={styles.card}>
          <div style={styles.cardIcon}>&#128101;</div>
          <h3 style={styles.cardTitle}>Team Dynamics Alignment</h3>
          <p style={styles.cardDescription}>
            Your report for "{genTeam.team_name}" is being generated.
          </p>
          <div style={styles.vbGeneratingStatus}>
            <div style={styles.pendingSpinner}></div>
            <div>
              <p style={styles.pendingStatusText}>Generating your team report...</p>
              <p style={styles.pendingStatusSub}>This usually takes 3-5 minutes. This page will update automatically.</p>
            </div>
          </div>
          {tdaPolling && (
            <p style={styles.pollingNote}>Checking for your report...</p>
          )}
        </div>
      );
    }

    // State: Has completed reports
    if (completedTeams.length > 0) {
      return (
        <div style={styles.card}>
          <div style={styles.cardIcon}>&#128101;</div>
          <h3 style={styles.cardTitle}>Team Dynamics Alignment</h3>
          <p style={styles.cardDescription}>
            {completedTeams.length === 1
              ? `Your team report for "${completedTeams[0].team_name}" is ready.`
              : `You have ${completedTeams.length} team reports.`}
          </p>
          {completedTeams.map((team) => (
            <div key={team.id} style={styles.vbReportRow}>
              <div style={styles.vbReportInfo}>
                <span style={styles.vbReportRole}>{team.team_name}</span>
                {team.created_at && (
                  <span style={styles.vbReportDate}>
                    {new Date(team.created_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'short', day: 'numeric'
                    })}
                  </span>
                )}
              </div>
              {team.report_url && (
                <a
                  href={team.report_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.vbDownloadSmall}
                >
                  Download
                </a>
              )}
            </div>
          ))}
          {/* Show draft teams if any */}
          {draftTeams.length > 0 && (
            <div style={{ marginTop: 12, width: '100%' }}>
              {draftTeams.map((team) => (
                <div key={team.id} style={styles.vbReportRow}>
                  <div style={styles.vbReportInfo}>
                    <span style={styles.vbReportRole}>{team.team_name}</span>
                    <span style={styles.vbReportDate}>Draft: questionnaire in progress</span>
                  </div>
                  <a
                    href={`/results/tda/questionnaire/${team.id}`}
                    style={styles.vbDownloadSmall}
                  >
                    Continue
                  </a>
                </div>
              ))}
            </div>
          )}
          <div style={styles.vbActions}>
            <a href="/results/tda/team-assembly" style={styles.vbRunAgainBtn}>
              Build Another Team
            </a>
          </div>
        </div>
      );
    }

    // State: Has draft teams (no completed)
    if (draftTeams.length > 0) {
      return (
        <div style={styles.card}>
          <div style={styles.cardIcon}>&#128101;</div>
          <h3 style={styles.cardTitle}>Team Dynamics Alignment</h3>
          <p style={styles.cardDescription}>
            You have {draftTeams.length === 1 ? 'a team' : `${draftTeams.length} teams`} in progress.
            Complete the team context form to generate your report.
          </p>
          {draftTeams.map((team) => (
            <div key={team.id} style={styles.vbReportRow}>
              <div style={styles.vbReportInfo}>
                <span style={styles.vbReportRole}>{team.team_name}</span>
                <span style={styles.vbReportDate}>
                  {team.report_status === 'questionnaire_complete' ? 'Ready to generate' : 'Draft'}
                </span>
              </div>
              <a
                href={`/results/tda/questionnaire/${team.id}`}
                style={styles.vbDownloadSmall}
              >
                Continue
              </a>
            </div>
          ))}
          <div style={{ marginTop: 16 }}>
            <a href="/results/tda/team-assembly" style={styles.vbRunAgainBtn}>
              Build Another Team
            </a>
          </div>
        </div>
      );
    }

    // State: Purchased but no teams yet
    return (
      <div style={styles.card}>
        <div style={styles.cardIcon}>&#128101;</div>
        <h3 style={styles.cardTitle}>Team Dynamics Alignment</h3>
        <p style={styles.cardDescription}>
          You've unlocked Team Dynamics Alignment! Build your team roster and
          discover how your team's communication patterns interact.
        </p>
        <a href="/results/tda/team-assembly" style={styles.downloadBtn}>
          Build Your Team
        </a>
      </div>
    );
  };

  // ===== Share Your Profile =====
  // Reports from the redesigned PASS report (version pass-report-4.x) save
  // what_i_bring / requests / practicing; earlier reports saved three other lists.
  const shareSections = (content) => {
    if (content && String(content.version || '').startsWith('pass-report-4')) {
      return [
        { heading: 'What I Tend to Bring', items: content.what_i_bring || [] },
        { heading: 'How We Can Work Well Together', items: content.requests || [] },
        { heading: 'What I Am Building On', items: content.practicing ? [content.practicing] : [] }
      ].filter(section => section.items.length);
    }
    return [
      { heading: 'I Communicate Best When...', items: content.communicate_best_when || [] },
      { heading: 'I Bring Extra Intention When...', items: content.bring_extra_intention_when || [] },
      { heading: 'To Work Effectively With Me...', items: content.to_work_effectively || [] }
    ];
  };

  const handleCopyShareText = async () => {
    if (!shareContent) return;
    const firstName = (profile?.full_name || 'Me').split(' ')[0];
    const lines = [];
    lines.push(`Working With ${firstName}`);
    if (profileTitle) lines.push(`Communication Style: "${profileTitle}"`);
    lines.push('');
    shareSections(shareContent).forEach(section => {
      lines.push(section.heading);
      section.items.forEach(item => lines.push(`  • ${item}`));
      lines.push('');
    });
    lines.pop();
    lines.push('');
    lines.push('---');
    lines.push('From my PASS Profile 3.0 report: https://pass.tedsvoiceacademy.com');
    const shareText = lines.join('\n');
    try {
      await navigator.clipboard.writeText(shareText);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 3000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = shareText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 3000);
    }
  };

  const renderShareCard = () => {
    // No share content yet — show teaser
    if (!shareContent) {
      return (
        <div style={{ ...styles.card, ...styles.cardComingSoon }}>
          <div style={styles.cardIcon}>&#128279;</div>
          <h3 style={styles.cardTitle}>Share Your Profile</h3>
          <p style={styles.cardDescription}>
            Your shareable "Working With Me" summary will appear here once your report is generated.
          </p>
          <div style={styles.comingSoonBadge}>Available After Report</div>
        </div>
      );
    }

    const firstName = (profile?.full_name || 'Me').split(' ')[0];

    return (
      <>
        <div style={styles.card}>
          <div style={styles.cardIcon}>&#128279;</div>
          <h3 style={styles.cardTitle}>Share Your Profile</h3>
          <p style={styles.cardDescription}>
            Share your "Working With Me" summary with colleagues, coaches, or team leads.
          </p>
          <p style={styles.shareStyleLabel}>
            <strong>"{profileTitle}"</strong>
          </p>
          <div style={styles.shareBtnRow}>
            <button
              onClick={handleCopyShareText}
              style={shareCopied ? { ...styles.downloadBtn, background: '#0B3D2E' } : styles.downloadBtn}
            >
              {shareCopied ? '\u2713 Copied!' : 'Copy to Clipboard'}
            </button>
            <button
              onClick={() => setShareExpanded(!shareExpanded)}
              style={styles.sharePreviewBtn}
            >
              {shareExpanded ? 'Hide Preview' : 'Preview'}
            </button>
          </div>
        </div>

        {shareExpanded && (
          <div style={styles.shareExpandedPanel}>
            <h4 style={styles.shareSectionTitle}>Working With {firstName}</h4>
            <p style={styles.shareStyleLabelExpanded}>Communication Style: <strong>"{profileTitle}"</strong></p>

            {shareSections(shareContent).map(section => (
              <React.Fragment key={section.heading}>
                <p style={styles.shareHeading}>{section.heading}</p>
                <ul style={styles.shareList}>
                  {section.items.map((item, i) => (
                    <li key={i} style={styles.shareListItem}>{item}</li>
                  ))}
                </ul>
              </React.Fragment>
            ))}

            <button
              onClick={() => setShareExpanded(false)}
              style={styles.shareCollapseBtn}
            >
              Close Preview
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          <button onClick={signOut} style={styles.signOutBtn}>Sign Out</button>
        </div>
      </header>

      {/* Main content */}
      <main style={styles.main}>
        <div style={styles.container}>

          {/* Welcome banner */}
          <div style={styles.welcomeBanner}>
            {profileTitle ? (
              <>
                <p style={styles.welcomeLabel}>Your Communication Style</p>
                <h2 style={styles.profileTitle}>{profileTitle}</h2>
              </>
            ) : (
              <h2 style={styles.profileTitle}>Welcome, {profile?.full_name || user?.email}</h2>
            )}
          </div>

          {reportReady ? (
            /* ===== DASHBOARD STATE ===== */
            <div style={styles.dashboardGrid}>

              {/* Report download card */}
              <div style={styles.card}>
                <div style={styles.cardIcon}>📄</div>
                <h3 style={styles.cardTitle}>Your PASS Profile Report</h3>
                <p style={styles.cardDescription}>
                  Your complete communication profile, ready to download.
                </p>
                {reportDate && (
                  <p style={styles.cardMeta}>
                    Generated {new Date(reportDate).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                )}
                <a
                  href={reportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.downloadBtn}
                >
                  Download Report (PDF)
                </a>
              </div>

              {/* Master Your PASS Profile course card — only if they bought it */}
              {renderCourseCard()}

              {/* Voice-Brand Alignment card — dynamic states */}
              {renderVoiceBrandCard()}

              {/* Team Dynamics Alignment card — visibility gated */}
              {renderTDACard()}

              {/* Share Your Profile card */}
              {renderShareCard()}

            </div>
          ) : (
            /* ===== PENDING STATE ===== */
            <div style={styles.pendingCard}>
              <h2 style={styles.pendingTitle}>Your Assessment is Complete!</h2>
              <p style={styles.pendingText}>
                Congratulations! You've completed all 60 scenarios.
              </p>
              <div style={styles.pendingStatus}>
                <div style={styles.pendingSpinner}></div>
                <div>
                  <p style={styles.pendingStatusText}>
                    {reportStatus === 'generating'
                      ? 'Your report is being generated...'
                      : 'Your report will be generated shortly...'}
                  </p>
                  <p style={styles.pendingStatusSub}>
                    This usually takes 3-5 minutes. This page will update automatically.
                  </p>
                  <p style={styles.pendingEmail}>
                    You'll also receive it via email at <strong>{user?.email}</strong>
                  </p>
                </div>
              </div>
              {polling && (
                <p style={styles.pollingNote}>Checking for your report...</p>
              )}
            </div>
          )}

          {/* Course access does not wait on the report — show it in either state.
              Rendered here so it appears for buyers who haven't finished the
              assessment yet, not just those with a completed report. */}
          {!reportReady && (
            <div style={styles.dashboardGrid}>
              {renderCourseCard()}
            </div>
          )}

        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>
          Questions? Contact us at{' '}
          <a href="mailto:PASS@tedsvoiceacademy.com" style={styles.footerLink}>
            PASS@tedsvoiceacademy.com
          </a>
        </p>
      </footer>
    </div>
  );
}

/* ===== STYLES ===== */
const styles = {
  page: {
    minHeight: '100vh',
    background: '#F8F9FA',
    fontFamily: 'Inter, system-ui, sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#F8F9FA',
  },
  loadingText: {
    color: '#6B7A8F',
    fontSize: 16,
  },

  // Header
  header: {
    backgroundColor: '#FFFFFF',
    borderBottom: '3px solid #2A9D8F',
    padding: '16px 24px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  headerInner: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    height: '40px',
  },
  signOutBtn: {
    background: 'transparent',
    border: '1px solid #0B3D2E',
    color: '#0B3D2E',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },

  // Main
  main: {
    flex: 1,
    padding: '32px 0',
  },
  container: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '0 24px',
  },

  // Welcome banner
  welcomeBanner: {
    background: 'linear-gradient(135deg, #0B3D2E 0%, #2A9D8F 100%)',
    borderRadius: 12,
    padding: '32px 40px',
    marginBottom: 32,
    textAlign: 'center',
  },
  welcomeLabel: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 14,
    margin: '0 0 8px 0',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  profileTitle: {
    color: 'white',
    fontSize: 28,
    fontWeight: 700,
    margin: 0,
    fontFamily: 'Outfit, Inter, sans-serif',
  },

  // Dashboard grid
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 24,
  },

  // Cards
  card: {
    background: 'white',
    borderRadius: 12,
    padding: '32px 28px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  cardComingSoon: {
    opacity: 0.7,
  },
  cardIcon: {
    fontSize: 32,
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 8px 0',
  },
  cardDescription: {
    fontSize: 14,
    color: '#6B7A8F',
    lineHeight: 1.6,
    margin: '0 0 16px 0',
    flex: 1,
  },
  cardMeta: {
    fontSize: 12,
    color: '#6B7A8F',
    margin: '0 0 16px 0',
  },
  courseEmailNote: {
    fontSize: 13,
    lineHeight: 1.5,
    color: '#4A5568',
    backgroundColor: '#F4F8F7',
    borderLeft: '3px solid #2A9D8F',
    padding: '10px 14px',
    borderRadius: 4,
    margin: '0 0 16px 0',
    textAlign: 'left',
  },
  // Share card styles
  shareStyleLabel: {
    fontSize: 14,
    color: '#2A9D8F',
    margin: '0 0 16px 0',
  },
  shareBtnRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  sharePreviewBtn: {
    display: 'inline-block',
    background: 'transparent',
    color: '#2A9D8F',
    padding: '12px 24px',
    borderRadius: 8,
    border: '1px solid #2A9D8F',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  shareExpandedPanel: {
    gridColumn: '1 / -1',
    background: '#F8FAF9',
    border: '1px solid #E0E8E4',
    borderRadius: 12,
    padding: '28px 32px',
    boxSizing: 'border-box',
  },
  shareSectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 4px 0',
  },
  shareStyleLabelExpanded: {
    fontSize: 14,
    color: '#2A9D8F',
    margin: '0 0 20px 0',
  },
  shareHeading: {
    fontSize: 15,
    fontWeight: 600,
    color: '#0B3D2E',
    margin: '16px 0 8px 0',
  },
  shareList: {
    margin: '0 0 8px 0',
    paddingLeft: 22,
  },
  shareListItem: {
    fontSize: 14,
    color: '#3A4A5C',
    lineHeight: 1.7,
    marginBottom: 6,
  },
  shareCollapseBtn: {
    display: 'inline-block',
    background: 'transparent',
    color: '#6B7A8F',
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid #E8ECF0',
    fontWeight: 500,
    fontSize: 13,
    cursor: 'pointer',
    marginTop: 16,
  },
  downloadBtn: {
    display: 'inline-block',
    background: '#2A9D8F',
    color: 'white',
    padding: '12px 24px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 14,
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  comingSoonBadge: {
    display: 'inline-block',
    background: '#E8ECF0',
    color: '#6B7A8F',
    padding: '8px 16px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
  },

  // Voice-Brand card states
  vbGeneratingStatus: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    textAlign: 'left',
    background: '#F0FAF4',
    padding: '16px 20px',
    borderRadius: 8,
    border: '1px solid #C8E6D0',
    width: '100%',
  },
  vbReportRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '10px 0',
    borderBottom: '1px solid #E8ECF0',
  },
  vbReportInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  vbReportRole: {
    fontSize: 14,
    fontWeight: 600,
    color: '#0B3D2E',
  },
  vbReportDate: {
    fontSize: 12,
    color: '#6B7A8F',
  },
  vbDownloadSmall: {
    display: 'inline-block',
    background: '#2A9D8F',
    color: 'white',
    padding: '6px 16px',
    borderRadius: 6,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 13,
  },
  vbActions: {
    marginTop: 16,
    width: '100%',
  },
  vbRunAgainBtn: {
    display: 'inline-block',
    background: 'transparent',
    color: '#0B3D2E',
    padding: '10px 20px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 14,
    border: '1px solid #0B3D2E',
  },

  // TDA purchase card
  tdaSizeSelector: {
    width: '100%',
    marginBottom: 12,
  },
  tdaSizeLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#0B3D2E',
    display: 'block',
    marginBottom: 8,
  },
  tdaSizeBtnRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 8,
  },
  tdaSizeBtn: {
    flex: 1,
    padding: '10px 8px',
    borderRadius: 8,
    border: '1px solid #D1D5DB',
    background: 'white',
    color: '#333',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
  tdaSizeBtnActive: {
    background: '#0B3D2E',
    color: 'white',
    border: '1px solid #0B3D2E',
  },
  tdaPriceDisplay: {
    fontSize: 14,
    color: '#333',
    margin: 0,
  },
  tdaError: {
    fontSize: 13,
    color: '#DC2626',
    margin: '0 0 8px 0',
  },

  // Pending state
  pendingCard: {
    background: 'white',
    borderRadius: 12,
    padding: '40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    textAlign: 'center',
    maxWidth: 600,
    margin: '0 auto',
  },
  pendingTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 8px 0',
  },
  pendingText: {
    fontSize: 16,
    color: '#6B7A8F',
    margin: '0 0 32px 0',
  },
  pendingStatus: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 20,
    textAlign: 'left',
    background: '#F0FAF4',
    padding: '20px 24px',
    borderRadius: 8,
    border: '1px solid #C8E6D0',
  },
  pendingSpinner: {
    width: 24,
    height: 24,
    border: '3px solid #C8E6D0',
    borderTop: '3px solid #2A9D8F',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    flexShrink: 0,
    marginTop: 2,
  },
  pendingStatusText: {
    fontSize: 15,
    fontWeight: 600,
    color: '#0B3D2E',
    margin: '0 0 4px 0',
  },
  pendingStatusSub: {
    fontSize: 13,
    color: '#6B7A8F',
    margin: '0 0 8px 0',
  },
  pendingEmail: {
    fontSize: 13,
    color: '#6B7A8F',
    margin: 0,
  },
  pollingNote: {
    fontSize: 12,
    color: '#6B7A8F',
    marginTop: 16,
    fontStyle: 'italic',
  },

  // Footer
  footer: {
    padding: '24px 0',
    textAlign: 'center',
    borderTop: '1px solid #E8ECF0',
  },
  footerText: {
    fontSize: 14,
    color: '#6B7A8F',
    margin: 0,
  },
  footerLink: {
    color: '#2A9D8F',
    textDecoration: 'none',
    fontWeight: 500,
  },
};

export default ClientPortal;
